/**
 * Coverage D business rules shape — mirrors `packages/brand-config-be/src/types.ts`
 * (`FarmRiskTypeConfig.businessRules.coverageD`). Defined locally so farm-utils
 * stays decoupled from brand-config-be.
 */
export type FarmCoverageDRules = {
  min?: number;
  max?: number;
  allowEditing?: boolean;
  percentOfCoverageA?: number;
  /**
   * Flat dollar seed for an editable Coverage D cell (#33149). Editable rules carry no
   * `percentOfCoverageA`, so without this the default resolves to $0 and the intended seed
   * (e.g. CFM's $3,000) never appears. Independent of Coverage A. Ignored when
   * `percentOfCoverageA` is set (a locked cell always derives from Coverage A).
   */
  defaultValue?: number;
};

export type FarmCoverageDBusinessRules = {
  byResidenceUsage?: Record<string, FarmCoverageDRules>;
  byLossSettlement?: Record<
    string,
    {
      byResidenceUsage: Record<string, FarmCoverageDRules>;
    }
  >;
};

const LEGACY_PERCENT_OF_COVERAGE_A = 0.2;

/**
 * Resolves the Coverage D rule entry for a given residence usage / loss settlement pair.
 *
 * FO2 defines rules via `byLossSettlement[…].byResidenceUsage[…]`; FO3 / FO1 use
 * `byResidenceUsage[…]` directly. We try the nested lookup first and fall through
 * to the flat one, matching the pattern used by `resolveFarmCoverageCRules` and
 * `farm-post-data-rules.ts`.
 */
export function resolveFarmCoverageDRules({
  residenceUsage,
  lossSettlement,
  businessRulesD
}: {
  residenceUsage?: string | null;
  lossSettlement?: string | null;
  businessRulesD?: FarmCoverageDBusinessRules;
}): FarmCoverageDRules | undefined {
  if (!businessRulesD) {
    return undefined;
  }

  if (businessRulesD.byLossSettlement && lossSettlement && residenceUsage) {
    const rules = businessRulesD.byLossSettlement[lossSettlement]?.byResidenceUsage?.[residenceUsage];
    if (rules) {
      return rules;
    }
  }

  if (businessRulesD.byResidenceUsage && residenceUsage) {
    return businessRulesD.byResidenceUsage[residenceUsage];
  }

  return undefined;
}

/**
 * Computes the default Coverage D value for a dwelling risk.
 *
 * Context: GitHub issues #30548 / follow-up — Forreston FO1/FO2/FO3 tenant-occupied
 * rentals must default to 10% of Coverage A. Primary/Seasonal stay at 20%, Vacant
 * stays at $0. The previous fix updated brand config + display components but left
 * eight hardcoded `coverageA * 0.2` write paths in the shared form code, which
 * stomped the brand-config value the moment Coverage A or Loss Settlement changed.
 *
 * Tenant rentals are back to 10% of Coverage A as of #32082 (the brief #32031
 * fixed-$3,000 rule was reverted), so they resolve via `percentOfCoverageA` below.
 *
 * Resolution order:
 *   1. Matched rule with `percentOfCoverageA` set → `coverageA * percentOfCoverageA`.
 *   2. Matched rule with a flat `defaultValue` (#33149, editable CFM cells) → that amount.
 *   3. Matched rule with neither (Vacant, or an editable cell that seeds $0) → `0`.
 *   4. No matched rule → legacy fallback `coverageA * 0.2`. This preserves behavior
 *      for any carrier/form/usage without configured rules.
 */
export function getDefaultFarmCoverageD({
  residenceUsage,
  lossSettlement,
  coverageA,
  businessRulesD
}: {
  residenceUsage?: string | null;
  lossSettlement?: string | null;
  coverageA: number;
  businessRulesD?: FarmCoverageDBusinessRules;
}): number {
  const rules = resolveFarmCoverageDRules({ residenceUsage, lossSettlement, businessRulesD });

  if (rules) {
    if (rules.percentOfCoverageA != null) {
      return Math.round(coverageA * rules.percentOfCoverageA);
    }
    // Editable cell with a flat seed (#33149, e.g. CFM's $3,000) → seed that amount.
    if (rules.defaultValue != null) {
      return rules.defaultValue;
    }
    // Matched rule with no percentOfCoverageA and no defaultValue (e.g. Vacant { min: 0, max: 0 },
    // or CFM FO1 editable $0–$3,000 which intentionally seeds $0) → $0.
    return 0;
  }

  return Math.round(coverageA * LEGACY_PERCENT_OF_COVERAGE_A);
}

/**
 * Whether Coverage D is an agent-editable field for the given residence usage / loss settlement.
 *
 * The shared FO1/FO2/FO3 risk forms auto-sync (force-write) Coverage D to `getDefaultFarmCoverageD`
 * whenever Coverage A, residence usage, or Coverage D itself changes. That is correct for carriers
 * (e.g. Forreston) whose brand config LOCKS Coverage D at a percentage of Coverage A
 * (`allowEditing: false`). It is wrong for CFM, whose filed MO spec (#31667 / #32560) makes FO1
 * Coverage D an editable $0–$3,000 field (`allowEditing: true`, no `percentOfCoverageA`): because an
 * editable rule has no percentage, the default resolves to $0, so the sync stomps every value the
 * agent types back to $0 and the "Update offer" button never appears (issue #33060).
 *
 * The forms consult this helper to skip enforcement for editable configs, preserving the
 * agent-entered value (bounds are enforced by validation instead). Forreston is unaffected — its
 * rules are `allowEditing: false`, so this returns `false` and the existing lock behavior stands.
 */
export function isFarmCoverageDEditable({
  residenceUsage,
  lossSettlement,
  businessRulesD
}: {
  residenceUsage?: string | null;
  lossSettlement?: string | null;
  businessRulesD?: FarmCoverageDBusinessRules;
}): boolean {
  return resolveFarmCoverageDRules({ residenceUsage, lossSettlement, businessRulesD })?.allowEditing === true;
}
